import { useState } from "react";

import { SourceBadge } from "@app/components/txn-table-panel";

import { COUNTED_VERDICTS, CandidatePicker, VERDICT_HINTS, VerbButton, cents, type Candidate } from "./shared";

export type ReviewItem = {
  readonly qboId: string;
  readonly entityType: string;
  readonly verdict: string;
  readonly amountCents: number;
  readonly txnDate?: string | null;
  readonly counterparty?: string | null;
  readonly category?: string | null;
  readonly deletedInQbo: boolean;
  readonly candidates: ReadonlyArray<Candidate>;
};

export type Filter = { pattern: string; bankAccountId: string; periodStart: string; periodEnd: string };

export const keyOf = (item: { qboId: string; entityType: string }) => `${item.entityType}:${item.qboId}`;

export function AllRecordsTab({
  items,
  totalCount,
  bankAccounts,
  filter,
  onFilter,
  selected,
  onSelect,
  busy,
  onFilterAction,
  onSelectedAction,
  onBind,
}: {
  items: ReadonlyArray<ReviewItem>;
  totalCount: number;
  bankAccounts: ReadonlyArray<{ readonly id: string; readonly nickname: string }>;
  filter: Filter;
  onFilter: (filter: Filter) => void;
  selected: ReadonlySet<string>;
  onSelect: (next: ReadonlySet<string>) => void;
  busy: boolean;
  onFilterAction: (verb: "CONFIRM" | "DISMISS") => void;
  onSelectedAction: (verb: "CONFIRM" | "DISMISS") => void;
  onBind: (item: ReviewItem, bankTxnId: string) => void;
}) {
  const [expanded, setExpanded] = useState<string | null>(null);
  const hasFilter = filter.pattern !== "" || filter.bankAccountId !== "" || filter.periodStart !== "" || filter.periodEnd !== "";
  const allSelected = items.length > 0 && selected.size === items.length;

  const toggle = (key: string) => {
    const next = new Set(selected);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onSelect(next);
  };

  return (
    <div className="space-y-4">
      {/* Bulk-by-filter bar (R20): the server resolves the matching keys, beyond the shown slice. */}
      <section className="rounded border bg-white p-3">
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-xs text-gray-500">
            Counterparty contains
            <input
              type="text"
              value={filter.pattern}
              onChange={(event) => onFilter({ ...filter, pattern: event.target.value })}
              placeholder="e.g. Stripe"
              className="mt-1 block rounded border px-2 py-1 text-sm text-gray-800"
            />
          </label>
          <label className="text-xs text-gray-500">
            Account
            <select
              value={filter.bankAccountId}
              onChange={(event) => onFilter({ ...filter, bankAccountId: event.target.value })}
              className="mt-1 block rounded border px-2 py-1 text-sm text-gray-800"
            >
              <option value="">Any</option>
              {bankAccounts.map((account) => (
                <option key={account.id} value={account.id}>
                  {account.nickname}
                </option>
              ))}
            </select>
          </label>
          <label className="text-xs text-gray-500">
            From
            <input
              type="date"
              value={filter.periodStart}
              onChange={(event) => onFilter({ ...filter, periodStart: event.target.value })}
              className="mt-1 block rounded border px-2 py-1 text-sm text-gray-800"
            />
          </label>
          <label className="text-xs text-gray-500">
            To
            <input
              type="date"
              value={filter.periodEnd}
              onChange={(event) => onFilter({ ...filter, periodEnd: event.target.value })}
              className="mt-1 block rounded border px-2 py-1 text-sm text-gray-800"
            />
          </label>
          <div className="ml-auto flex gap-2">
            <VerbButton verb="DISMISS" disabled={!hasFilter || busy} onClick={() => onFilterAction("DISMISS")} className="px-3 py-1.5 text-sm" />
            <VerbButton verb="CONFIRM" disabled={!hasFilter || busy} onClick={() => onFilterAction("CONFIRM")} className="px-3 py-1.5 text-sm" />
          </div>
        </div>
        <p className="mt-1 text-xs text-gray-400">
          Filter actions resolve every matching record server-side — including records beyond the shown slice — and ask first when they are large.
        </p>
      </section>

      {selected.size > 0 && (
        <div className="flex items-center gap-3 rounded bg-gray-900 px-4 py-2 text-sm text-white">
          <span>{selected.size} selected</span>
          <button type="button" disabled={busy} onClick={() => onSelectedAction("DISMISS")} className="rounded bg-white px-2 py-1 text-xs text-gray-900 hover:bg-gray-100 disabled:opacity-50">
            Exclude
          </button>
          <button type="button" disabled={busy} onClick={() => onSelectedAction("CONFIRM")} className="rounded bg-white px-2 py-1 text-xs text-gray-900 hover:bg-gray-100 disabled:opacity-50">
            Keep
          </button>
          <button type="button" onClick={() => onSelect(new Set())} className="ml-auto text-xs underline hover:no-underline">
            Clear
          </button>
        </div>
      )}

      <section className="rounded border bg-white">
        {items.length === 0 ? (
          <p className="px-4 py-6 text-sm text-gray-400">No records.</p>
        ) : (
          <>
            {totalCount > items.length && (
              <p className="px-4 py-2 text-xs text-gray-400">
                Showing the latest {items.length.toLocaleString()} of {totalCount.toLocaleString()} — the Decide tab reaches every record by counterparty.
              </p>
            )}
            <table className="w-full text-sm">
              <thead className="border-b bg-gray-50 text-xs text-gray-500">
                <tr>
                  <th className="w-8 px-3 py-2">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={() => onSelect(allSelected ? new Set() : new Set(items.map(keyOf)))}
                      aria-label="Select all"
                    />
                  </th>
                  <th className="px-3 py-2 text-left font-medium">Date</th>
                  <th className="px-3 py-2 text-left font-medium">Counterparty</th>
                  <th className="px-3 py-2 text-left font-medium">QBO account</th>
                  <th className="px-3 py-2 text-left font-medium">Record</th>
                  <th className="px-3 py-2 text-left font-medium">In your books</th>
                  <th className="px-3 py-2 text-right font-medium">Amount</th>
                  <th className="px-3 py-2 text-right font-medium" />
                </tr>
              </thead>
              <tbody className="divide-y">
                {items.map((item) => {
                  const key = keyOf(item);
                  const open = expanded === key;
                  const counted = COUNTED_VERDICTS.has(item.verdict);
                  return (
                    <ItemRows
                      key={key}
                      item={item}
                      counted={counted}
                      selected={selected.has(key)}
                      onToggle={() => toggle(key)}
                      expanded={open}
                      onExpand={() => setExpanded(open ? null : key)}
                      onBind={(bankTxnId) => onBind(item, bankTxnId)}
                      busy={busy}
                    />
                  );
                })}
              </tbody>
            </table>
          </>
        )}
      </section>
    </div>
  );
}

function ItemRows({
  item,
  counted,
  selected,
  onToggle,
  expanded,
  onExpand,
  onBind,
  busy,
}: {
  item: ReviewItem;
  counted: boolean;
  selected: boolean;
  onToggle: () => void;
  expanded: boolean;
  onExpand: () => void;
  onBind: (bankTxnId: string) => void;
  busy: boolean;
}) {
  return (
    <>
      <tr className={selected ? "bg-amber-50" : "hover:bg-gray-50"}>
        <td className="px-3 py-2 align-top">
          <input type="checkbox" checked={selected} onChange={onToggle} aria-label={`Select ${item.qboId}`} />
        </td>
        <td className="whitespace-nowrap px-3 py-2 align-top tabular-nums text-gray-500">{item.txnDate ?? "—"}</td>
        <td className="px-3 py-2 align-top text-gray-800">{item.counterparty ?? "—"}</td>
        <td className="px-3 py-2 align-top text-gray-500">{item.category ?? "unmapped"}</td>
        <td className="whitespace-nowrap px-3 py-2 align-top text-xs text-gray-400">
          {item.entityType} #{item.qboId}
          {item.deletedInQbo && <span className="ml-1 text-red-500">deleted in QBO</span>}
          <span className="ml-2 inline-block align-middle">
            <SourceBadge kind="" flagged={counted} />
          </span>
        </td>
        <td className="px-3 py-2 align-top">
          <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${counted ? "bg-amber-50 text-amber-700" : "bg-gray-100 text-gray-500"}`}>
            {counted ? "Counted" : "Not counted"}
          </span>
        </td>
        <td className={`whitespace-nowrap px-3 py-2 text-right align-top font-medium tabular-nums ${item.amountCents < 0 ? "text-red-600" : "text-green-600"}`}>
          {cents(item.amountCents)}
        </td>
        <td className="px-3 py-2 text-right align-top">
          <button type="button" onClick={onExpand} className="text-xs text-indigo-600 underline hover:no-underline">
            {expanded ? "close" : item.candidates.length > 0 ? `${item.candidates.length} candidate${item.candidates.length === 1 ? "" : "s"}` : "details"}
          </button>
        </td>
      </tr>
      {expanded && (
        <tr className="bg-indigo-50/40">
          <td />
          <td colSpan={7} className="px-3 py-2">
            <p className="mb-2 text-xs text-gray-500">
              <span className="font-medium text-gray-700">{item.verdict.toLowerCase()}</span> — {VERDICT_HINTS[item.verdict] ?? ""}
            </p>
            <CandidatePicker candidates={item.candidates} busy={busy} onBind={onBind} />
          </td>
        </tr>
      )}
    </>
  );
}
